import React, { useEffect, useMemo, useState } from "react";
import Layout from "../layout/Layout";
import LoadingOverlay from "react-loading-overlay-ts";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import {
  fetchFaqCategories,
  createFaqCategory,
  updateFaqCategory,
  deleteFaqCategory,
  fetchFaqItemsByCategory,
  createFaqItem,
  updateFaqItem,
  deleteFaqItem
} from "../Redux/Action/FaqAction";

export default function FaqManagement() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: "", order: 0 });
  const [editCategoryId, setEditCategoryId] = useState("");

  const [showItemModal, setShowItemModal] = useState(false);
  const [itemForm, setItemForm] = useState({ question: "", answer: "", order: 0 });
  const [editItemId, setEditItemId] = useState("");

  const sortedCategories = useMemo(() => {
    return (categories || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [categories]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await dispatch(fetchFaqCategories()).unwrap();
      setCategories(res || []);
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async (categoryId) => {
    setLoading(true);
    try {
      const res = await dispatch(fetchFaqItemsByCategory(categoryId)).unwrap();
      setItems(res || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);
  useEffect(() => {
    if (!selectedCategory && sortedCategories.length > 0) {
      const first = sortedCategories[0];
      setSelectedCategory(first);
      loadItems(first._id);
    }
  }, [sortedCategories, selectedCategory]);

  const onSelectCategory = async (cat) => {
    setSelectedCategory(cat);
    await loadItems(cat._id);
  };

  const openAddCategory = () => {
    setEditCategoryId("");
    setCategoryForm({ name: "", order: (categories?.length || 0) + 1 });
    setShowCategoryModal(true);
  };

  const openEditCategory = (cat) => {
    setEditCategoryId(cat._id);
    setCategoryForm({ name: cat.name, order: cat.order || 0 });
    setShowCategoryModal(true);
  };

  const submitCategory = async () => {
    setLoading(true);
    if (editCategoryId) {
      await dispatch(updateFaqCategory({ categoryId: editCategoryId, data: categoryForm })).unwrap();
    } else {
      await dispatch(createFaqCategory(categoryForm)).unwrap();
    }
    setShowCategoryModal(false);
    await loadCategories();
    setLoading(false);
  };

  const removeCategory = async (catId) => {
    setLoading(true);
    await dispatch(deleteFaqCategory(catId)).unwrap();
    if (selectedCategory?._id === catId) {
      setSelectedCategory(null);
      setItems([]);
    }
    await loadCategories();
    setLoading(false);
  };

  const openAddItem = () => {
    if (!selectedCategory) return;
    setEditItemId("");
    setItemForm({ question: "", answer: "", order: (items?.length || 0) + 1 });
    setShowItemModal(true);
  };

  const openEditItem = (it) => {
    setEditItemId(it._id);
    setItemForm({ question: it.question, answer: it.answer, order: it.order || 0 });
    setShowItemModal(true);
  };

  const submitItem = async () => {
    if (!selectedCategory) return;
    setLoading(true);
    if (editItemId) {
      await dispatch(updateFaqItem({ itemId: editItemId, data: itemForm })).unwrap();
    } else {
      await dispatch(createFaqItem({ categoryId: selectedCategory._id, ...itemForm })).unwrap();
    }
    setShowItemModal(false);
    await loadItems(selectedCategory._id);
    setLoading(false);
  };

  const removeItem = async (itemId) => {
    if (!selectedCategory) return;
    setLoading(true);
    await dispatch(deleteFaqItem(itemId)).unwrap();
    await loadItems(selectedCategory._id);
    setLoading(false);
  };

  const filteredItems = useMemo(() => {
    if (!search) return items;
    const s = search.toLowerCase();
    return (items || []).filter(
      (it) =>
        it.question.toLowerCase().includes(s) ||
        (it.answer || "").toLowerCase().includes(s)
    );
  }, [items, search]);
  const sortedItems = useMemo(() => {
    return (items || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [items]);
  const filteredSortedItems = useMemo(() => {
    return (filteredItems || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [filteredItems]);

  const moveItem = async (item, direction) => {
    if (!selectedCategory) return;
    const list = sortedItems;
    const index = list.findIndex((i) => i._id === item._id);
    if (index < 0) return;
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= list.length) return;
    const other = list[targetIndex];
    const currentOrder = item.order || 0;
    const otherOrder = other.order || 0;
    setLoading(true);
    try {
      await dispatch(updateFaqItem({ itemId: item._id, data: { order: otherOrder } })).unwrap();
      await dispatch(updateFaqItem({ itemId: other._id, data: { order: currentOrder } })).unwrap();
      await loadItems(selectedCategory._id);
    } finally {
      setLoading(false);
    }
  };

  const moveCategory = async (cat, direction) => {
    const list = sortedCategories;
    const index = list.findIndex((c) => c._id === cat._id);
    if (index < 0) return;
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= list.length) return;
    const other = list[targetIndex];
    const currentOrder = cat.order || 0;
    const otherOrder = other.order || 0;
    setLoading(true);
    try {
      await dispatch(updateFaqCategory({ categoryId: cat._id, data: { order: otherOrder } })).unwrap();
      await dispatch(updateFaqCategory({ categoryId: other._id, data: { order: currentOrder } })).unwrap();
      await loadCategories();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <LoadingOverlay active={loading} spinner text="Loading...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">
            <div className="modern-user-section-card" style={{ margin: 12, background: "#fff" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search questions/answers"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ maxWidth: 320, height: 32, fontSize: 13 }}
                />
                <button
                  className="common-btn"
                  style={{ padding: "4px 10px", fontSize: 12, height: 32 }}
                  onClick={openAddCategory}
                >
                  + Add Category
                </button>
                <button
                  className="common-btn"
                  style={{ padding: "4px 10px", fontSize: 12, height: 32, opacity: selectedCategory ? 1 : 0.6 }}
                  onClick={openAddItem}
                  disabled={!selectedCategory}
                >
                  + Add FAQ
                </button>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-md-4">
                <div className="modern-user-section-card" style={{ background: "#fff" }}>
                  <div className="d-flex justify-content-between align-items-center" style={{ paddingBottom: 8 }}>
                    <h5 className="mb-0" style={{ color: "#034328" }}>Categories</h5>
                  </div>
                  <div className="content-view-wrap">
                    <ul className="list-group">
                      {(sortedCategories || []).map((cat, idx) => (
                        <li
                          key={cat._id}
                          className="list-group-item d-flex justify-content-between align-items-center"
                          style={{
                            cursor: "pointer",
                            background: selectedCategory?._id === cat._id ? "#e8f9f2" : "#fff",
                            color: selectedCategory?._id === cat._id ? "#034328" : "#111"
                          }}
                          onClick={() => onSelectCategory(cat)}
                        >
                          <span style={{ fontWeight: 600 }}>{cat.name}</span>
                          <div
                            className="d-flex"
                            style={{ gap: 8, flexWrap: "wrap", maxWidth: 180, alignItems: "center" }}
                          >
                            <i
                              className="fas fa-arrow-up"
                              title="Move up"
                              style={{
                                cursor: idx === 0 ? "not-allowed" : "pointer",
                                opacity: idx === 0 ? 0.3 : 1,
                                fontSize: 14
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                idx > 0 && moveCategory(cat, -1);
                              }}
                            ></i>
                            <i
                              className="fas fa-arrow-down"
                              title="Move down"
                              style={{
                                cursor: idx === sortedCategories.length - 1 ? "not-allowed" : "pointer",
                                opacity: idx === sortedCategories.length - 1 ? 0.3 : 1,
                                fontSize: 14
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                idx < sortedCategories.length - 1 && moveCategory(cat, 1);
                              }}
                            ></i>
                            <i
                              className="fas fa-pencil"
                              title="Edit"
                              style={{ cursor: "pointer", fontSize: 14 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditCategory(cat);
                              }}
                            ></i>
                            <i
                              className="fas fa-trash"
                              title="Delete"
                              style={{ cursor: "pointer", fontSize: 14 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeCategory(cat._id);
                              }}
                            ></i>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-md-8">
                <div className="modern-user-section-card" style={{ background: "#fff" }}>
                  <div className="d-flex justify-content-between align-items-center" style={{ paddingBottom: 8 }}>
                    <h5 className="mb-0" style={{ color: "#034328" }}>
                      {selectedCategory ? `FAQs — ${selectedCategory.name}` : "FAQs"}
                    </h5>
                  </div>
                  <div className="content-view-wrap">
                    {selectedCategory ? (
                      <div className="list-group">
                        {(filteredSortedItems || []).map((it) => (
                          <div key={it._id} className="list-group-item">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <strong className="d-block" style={{ color: "#034328" }}>{it.question}</strong>
                                <div style={{ whiteSpace: "pre-wrap", color: "#374151" }}>{it.answer}</div>
                              </div>
                              <div className="d-flex" style={{ gap: 8, alignItems: "center" }}>
                                <i
                                  className="fas fa-arrow-up"
                                  style={{
                                    cursor: sortedItems[0]?._id === it._id ? "not-allowed" : "pointer",
                                    opacity: sortedItems[0]?._id === it._id ? 0.3 : 1,
                                    fontSize: 14
                                  }}
                                  onClick={() => moveItem(it, -1)}
                                ></i>
                                <i
                                  className="fas fa-arrow-down"
                                  style={{
                                    cursor: sortedItems[sortedItems.length - 1]?._id === it._id ? "not-allowed" : "pointer",
                                    opacity: sortedItems[sortedItems.length - 1]?._id === it._id ? 0.3 : 1,
                                    fontSize: 14
                                  }}
                                  onClick={() => moveItem(it, 1)}
                                ></i>
                                <i
                                  className="fas fa-pencil"
                                  title="Edit"
                                  style={{ cursor: "pointer", fontSize: 14 }}
                                  onClick={() => openEditItem(it)}
                                ></i>
                                <i
                                  className="fas fa-trash"
                                  title="Delete"
                                  style={{ cursor: "pointer", fontSize: 14 }}
                                  onClick={() => removeItem(it._id)}
                                ></i>
                              </div>
                            </div>
                          </div>
                        ))}
                        {filteredSortedItems.length === 0 && (
                          <div className="list-group-item text-muted">
                            No FAQs found for this category.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-muted p-3">Select a category to manage FAQs</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LoadingOverlay>

      <Modal show={showCategoryModal} onHide={() => setShowCategoryModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editCategoryId ? "Edit Category" : "Add Category"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group mb-3">
            <label>Name</label>
            <input
              className="form-control"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Payments & Pricing"
            />
          </div>
          <div className="form-group">
            <label>Order</label>
            <input
              type="number"
              className="form-control"
              value={categoryForm.order}
              onChange={(e) => setCategoryForm((f) => ({ ...f, order: Number(e.target.value) }))}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="common-btn" style={{ background: "#ccc", color: "#000" }} onClick={() => setShowCategoryModal(false)}>
            Cancel
          </button>
          <button className="common-btn" onClick={submitCategory}>
            Save
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showItemModal} onHide={() => setShowItemModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editItemId ? "Edit FAQ" : "Add FAQ"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group mb-3">
            <label>Question</label>
            <input
              className="form-control"
              value={itemForm.question}
              onChange={(e) => setItemForm((f) => ({ ...f, question: e.target.value }))}
              placeholder="Enter question"
            />
          </div>
          <div className="form-group mb-3">
            <label>Answer</label>
            <textarea
              className="form-control"
              rows={5}
              value={itemForm.answer}
              onChange={(e) => setItemForm((f) => ({ ...f, answer: e.target.value }))}
              placeholder="Enter answer (supports multi-line)"
            />
          </div>
          <div className="form-group">
            <label>Order</label>
            <input
              type="number"
              className="form-control"
              value={itemForm.order}
              onChange={(e) => setItemForm((f) => ({ ...f, order: Number(e.target.value) }))}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="common-btn" style={{ background: "#ccc", color: "#000" }} onClick={() => setShowItemModal(false)}>
            Cancel
          </button>
          <button className="common-btn" onClick={submitItem}>
            Save
          </button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}
